import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store/store';
import ThemeContext from "./context/ThemeContext";
import { i18nInitPromise } from './i18n';
import { APIProvider } from '@vis.gl/react-google-maps';
import { BranchProvider } from './context/BranchContext';
import { LoggedUserProvider } from './context/LoggedUserContext';
import { PROJECT_DOCUMENT_TITLE } from './config/branding';

document.title = PROJECT_DOCUMENT_TITLE;

// Quando sai um deploy, os arquivos de /static/ ganham nomes novos e os antigos deixam
// de existir. Quem estava com a aba aberta continua com o index.html velho em memória e,
// ao abrir uma tela lazy (React.lazy), tenta baixar um chunk que sumiu -> tela branca.
// O usuário leigo não sabe que precisa dar Ctrl+Shift+R, então recarregamos sozinhos.
// Guardamos o horário do último reload automático: se o erro persistir (servidor fora do
// ar, e não deploy novo), não entramos em loop de refresh; passada a janela, um deploy
// futuro volta a poder disparar o reload normalmente.
const CHUNK_RELOAD_KEY = 'colmeia:chunk-reloaded-at';
const CHUNK_RELOAD_COOLDOWN_MS = 60000;

function isChunkLoadError(message) {
  return /Loading chunk|ChunkLoadError|Loading CSS chunk|dynamically imported module/i.test(message || '');
}

function reloadOnceForNewVersion(message) {
  if (!isChunkLoadError(message)) return;

  const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
  if (Date.now() - lastReloadAt < CHUNK_RELOAD_COOLDOWN_MS) return;

  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  window.location.reload();
}

window.addEventListener('error', (event) => reloadOnceForNewVersion(event.message));
window.addEventListener('unhandledrejection', (event) => reloadOnceForNewVersion(event.reason?.message));

// Não antecipa o manifest genérico nas páginas públicas: navegadores mobile podem
// mantê-lo em cache e ignorar o manifest do tenant injetado após carregar a empresa.
const usesRuntimeManifest = /^\/(booking|p|portal)(\/|$)/.test(window.location.pathname);
if (!usesRuntimeManifest) {
  const manifestLink = document.createElement('link');
  manifestLink.rel = 'manifest';
  manifestLink.href = '/site.webmanifest';
  document.head.appendChild(manifestLink);
}

i18nInitPromise.then(() => {
  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
  <React.StrictMode>
    <Provider store={store}>
      <BrowserRouter basename='/'>
        <ThemeContext>
          <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
            <BranchProvider>
              <LoggedUserProvider>
                <App />
              </LoggedUserProvider>
            </BranchProvider>
          </APIProvider>
        </ThemeContext>
      </BrowserRouter>
    </Provider>
  </React.StrictMode>
  );

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
  reportWebVitals();
});
